import React from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useParams } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { RouteConfig, ROUTES } from "./config";
import { clearAuthTokenFromLocalStorage, getAuthTokenFromLocalStorage } from "hooks/useAuthToken";
import userService from "api/user/user.service";
import { useDispatch, useSelector } from "react-redux";
import { setCheckingToken, setIsAuthorized, setTeamSuspended, setUser } from "store/commonSlice";
import { setCreditMultiplier, setPackageList } from "store/creditSlice";
import paymentService from "api/payment/payment.service";
import MobileWarningPageBase from "./MobileWarningPage";
import AuthorizationPage from "./AuthorizationPage";
import { useLocalStorage } from "hooks/useLocalStorage";
import { CLIENT_ID_LOCAL_STORAGE_KEY } from "utils/constants";
import { showSnackbar } from "store/snackbarSlice";
import RouteThemeWrapper from "./RouteThemeWrapper";
import { useTheme } from "context/ThemeContext";
import { PATHS } from "./config/paths";
import usePageTracking from "hooks/usePageTracking";
import { setAnalyticsUser, clearAnalyticsUser } from "utils/analytics";
import i18n from "i18n";
import { applyUserPreferredLocale } from "i18n/detect";
import type { RootState } from "store";
import { SocketProvider } from "context/SocketContext";
import { MiroJobsProvider } from "context/MiroJobsContext";
import MiroJobsResumer from "components/MiroJobsResumer";
import useSessionRefresh from "hooks/useSessionRefresh";
import { SuspendedGate } from "components/TeamSuspended";
import { isSuspendedSession } from "utils/teamSuspended";
import ReplayGateSync from "./ReplayGateSync";

const NotFound = React.lazy(() => import("../pages/NotFound"));
const UnderConstruction = React.lazy(() => import("../pages/UnderConstruction/UnderConstruction"));
const PhotoshootRedirect = React.lazy(() => import("../pages/PhotoshootRedirect"));
// Developer docs preview (K104): the docs site is its own folder, src/docs-site (moving out — its MOVE.md).
const DocsApp = React.lazy(() => import("../docs-site/DocsApp"));

/** The docs preview reads and sets the APP's theme (one owner of <html> and of the stored choice), so
 *  a theme picked in the docs is the app's theme when the visitor navigates back. */
const EmbeddedDocs = () => {
  const { theme, setTheme } = useTheme();

  return <DocsApp theme={{ theme, setTheme }} />;
};

/** `/reports/<id>` → the board's own URL. A param-carrying redirect needs a component: `<Navigate>`
 *  takes a literal `to`, so the id has to be read off the route first. REF-6484 — see the route. */
const LegacyMoodboardReportRedirect = () => {
  const { id } = useParams<{ id?: string }>();

  return <Navigate to={id ? PATHS.MOODBOARDS_MY_DETAIL.replace(":id", id) : PATHS.MOODBOARDS} replace />;
};

/** A page that moved: its new path with the old one's params put in and its query kept (a shared filtered link). */
const MovedRedirect = ({ to }: { to: string }) => {
  const params = useParams();
  const { search } = useLocation();
  const path = to.replace(/:(\w+)/g, (_, name: string) => encodeURIComponent(params[name] ?? ""));

  return <Navigate to={`${path}${search}`} replace />;
};

const getRoute = (route: RouteConfig) => {
  const Component = route.component;
  const hasLayout = !!route.layout;
  const Layout = route.layout ?? React.Fragment;
  const restrictOnMobile = !route.isResponsive;
  // Pages with a Layout render the mobile restriction INSIDE the content area (header + nav stay usable).
  // Layout-less routes have no shell to preserve, so fall back to the full-page warning.
  const MobileWarningPage = !hasLayout && restrictOnMobile ? MobileWarningPageBase : React.Fragment;
  const layoutProps = hasLayout ? { ...route.layoutProps, restrictOnMobile } : undefined;

  return (
    <Route
      key={route.path}
      path={route.path}
      element={
        <ErrorBoundary>
          <RouteThemeWrapper theme={route.theme}>
            <AuthorizationPage
              routePath={route.path}
              requireLogin={route.requireLogin}
              paidPlanNeeded={route.paidPlanNeeded}
              requiredPermission={route.requiredPermission}
              permissionMode={route.permissionMode}
              permissionRedirect={route.permissionRedirect}
              permissionScreen={route.permissionScreen}
              permissionFallback={route.permissionFallback}
            >
              <MobileWarningPage>
                <Layout {...layoutProps}>
                  <React.Suspense fallback={null}>
                    <Component />
                  </React.Suspense>
                </Layout>
              </MobileWarningPage>
            </AuthorizationPage>
          </RouteThemeWrapper>
        </ErrorBoundary>
      }
    >
      {route?.children?.map((child) => getRoute(child))}
    </Route>
  );
};

import { EventDispatchLogout, EventDispatchSetPassword, EventDispatchSnackbar, EventDispatchTeamSuspended } from "./events";
import SiteImagesBoot, { SitePreviewBanner } from "components/SiteImagesBoot";

export { EventDispatchLogout, EventDispatchSetPassword, EventDispatchSnackbar };

const Router = () => {
  const dispatch = useDispatch();
  const userLanguage = useSelector((state: RootState) => state.common.user.language);
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

  // Hydrate i18n from BE user_info.language on every setUser. applyUserPreferredLocale
  // honors localStorage as the source of truth for explicit user toggles, so re-fetching
  // user data never clobbers a manual locale switch.
  React.useEffect(() => {
    applyUserPreferredLocale(userLanguage);
  }, [userLanguage]);

  // Check if we're in maintenance mode
  if (isMaintenanceMode) {
    return (
      <BrowserRouter>
        <ReplayGateSync />
        <Routes>
          <Route
            path={PATHS.MAINTENANCE}
            element={
              <ErrorBoundary>
                <React.Suspense fallback={null}>
                  <UnderConstruction />
                </React.Suspense>
              </ErrorBoundary>
            }
          />
          <Route path="*" element={<Navigate to={PATHS.MAINTENANCE} replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  React.useEffect(() => {
    if (getAuthTokenFromLocalStorage() !== null)
      userService
        .tokenControl({ client_id: useLocalStorage().getItem(CLIENT_ID_LOCAL_STORAGE_KEY) })
        .then((res: any) => {
          if (res.responseCode === 402) {
            // 402 means user doesn't have a paid plan
            dispatch(
              setUser({
                id: res?.responseData?.uid,
                email: res.responseData?.username ?? res.responseData?.email,
              })
            );
            dispatch(setIsAuthorized(false));
            dispatch(setCheckingToken(false));

            return;
          }
          // STRIPE P5-F2 (PD7, v3 P5-B11): a suspended team's session is real — keep it and show the suspended-team screen
          // (components/TeamSuspended). With TEAM_SUSPENDED_403 off, v3 answers 401 here instead: today's logout, unchanged.
          if (isSuspendedSession(res)) {
            dispatch(setUser({ id: res.responseData.uid, email: res.responseData?.username ?? res.responseData?.email }));
            dispatch(setTeamSuspended(true));
            dispatch(setIsAuthorized(false));
            dispatch(setCheckingToken(false));

            return;
          }
          if (res.responseData.uid) {
            dispatch(setIsAuthorized(true));
            dispatch(setCheckingToken(false));
            setAnalyticsUser(res.responseData.uid, res.responseData?.language);
            // Id only: no email or username reaches Sentry (REF-6708 privacy A2).
            Sentry.setUser({ id: res.responseData.uid });

            dispatch(
              setUser({
                id: res?.responseData?.uid,
                email: res.responseData?.username ?? res.responseData?.email,
                packageCode: res.responseData?.pt,
                language: res.responseData?.language,
              })
            );

            // Fetch credit multiplier after successful auth
            paymentService
              .getCreditMultiplier()
              .then((creditRes) => {
                if (creditRes.responseData) {
                  dispatch(setCreditMultiplier(creditRes.responseData));
                }
              })
              .catch(() => {
                // Silently fail - credit multiplier is non-critical
              });

            // Fetch package list after successful auth
            paymentService
              .getPackageList()
              .then((pkgRes: any) => {
                if (pkgRes.data?.package_list) {
                  dispatch(setPackageList(pkgRes.data.package_list));
                }
              })
              .catch(() => {
                // Silently fail - package list is non-critical
              });
          } else {
            dispatch(setIsAuthorized(false));
            dispatch(setCheckingToken(false));
            window.dispatchEvent(new CustomEvent(EventDispatchLogout));
          }
        })
        .catch(() => {
          // Never log the error: its axios config carries the session bearer token, and console output
          // reaches Sentry breadcrumbs and Session Replay (REF-6708).
          dispatch(setIsAuthorized(false));
          dispatch(setCheckingToken(false));
          window.dispatchEvent(new CustomEvent(EventDispatchLogout));
        });
    else dispatch(setCheckingToken(false));
  }, []);

  React.useEffect(() => {
    const detectPermissionDenied = () => {
      clearAnalyticsUser();
      Sentry.setUser(null);
      clearAuthTokenFromLocalStorage();
      useLocalStorage().removeItem(CLIENT_ID_LOCAL_STORAGE_KEY);
      window.location.href = "/";
    };
    window.addEventListener(EventDispatchLogout, detectPermissionDenied);

    // REF-5421: hard-gate backstop. A must_set_password 403 from any endpoint routes the
    // guest to the set-password screen (not a logout). The token stays — the set-password
    // POST is authed with it. Full-load nav guarantees a clean slate; skip if already there.
    const goSetPassword = () => {
      if (window.location.pathname.startsWith("/set-password")) return;
      window.location.href = "/set-password";
    };
    window.addEventListener(EventDispatchSetPassword, goSetPassword);

    // STRIPE P5-F2 (PD7): a gated route answered 403 team_suspended (HttpClient) — the session stays, the app gives way to
    // the suspended-team screen.
    const showTeamSuspended = () => dispatch(setTeamSuspended(true));
    window.addEventListener(EventDispatchTeamSuspended, showTeamSuspended);

    const handleEvent = (e: any) => {
      const detail = e.detail ?? {};

      // `messageKey` is the canonical contract (Phase 5-6). Callers that need a fallback localized
      // message when no specific key is available should send `messageKey: "snackbar:<status>.default"`.
      const localizedMessage = detail.messageKey ? i18n.t(detail.messageKey, detail.params ?? {}) : "";

      switch (detail.type) {
        case 402:
          dispatch(
            showSnackbar({
              type: 402,
              screen: detail.screen,
              panel: detail.panel ?? "",
              messageType: detail.messageType ?? "",
              popupType: detail.popupType ?? "",
              message: localizedMessage,
              popupReason: detail.popupReason ?? "",
              requiredCredits: detail.requiredCredits ?? null,
              remainingCredits: detail.remainingCredits ?? null,
            })
          );
          break;
        case 409:
          // `message` must be forwarded: WarningSnackbar renders `message ? message : getText(type)`,
          // so dropping it here made every 409 read as the generic `snackbar:409.default` even when
          // the interceptor had resolved a specific `errors:<code>` key (e.g. share_mode_conflict).
          dispatch(showSnackbar({ type: 409, message: localizedMessage }));
          break;
        case 503:
          dispatch(showSnackbar({ type: 503, message: localizedMessage }));
          break;
        default:
          break;
      }
    };
    window.addEventListener(EventDispatchSnackbar, handleEvent);

    return () => {
      window.removeEventListener(EventDispatchLogout, detectPermissionDenied);
      window.removeEventListener(EventDispatchSetPassword, goSetPassword);
      window.removeEventListener(EventDispatchTeamSuspended, showTeamSuspended);
      window.removeEventListener(EventDispatchSnackbar, detectPermissionDenied);
    };
  }, []);

  return (
    <BrowserRouter>
      <ReplayGateSync />
      <SessionRefresh />
      {/* Backoffice "Site Görselleri": above the routes so the signed-out login screen gets them too. */}
      <SiteImagesBoot />
      <SitePreviewBanner />
      {/* STRIPE P5-F2: while suspended, neither the routes nor the WebSocket provider mount (v3 closes the handshake). */}
      <SuspendedGate>
        <AppSocketProvider>
          {/* Miro send / import / moodboard jobs outlive the dialog that started them (REF-6702). */}
          <MiroJobsProvider>
            <MiroJobsResumer />
            <AppRoutes />
          </MiroJobsProvider>
        </AppSocketProvider>
      </SuspendedGate>
    </BrowserRouter>
  );
};

/** Renews the access token before its 24h expiry while signed in (`hooks/useSessionRefresh`). Renders nothing. */
const SessionRefresh = () => {
  useSessionRefresh();

  return null;
};

/**
 * The app's single WebSocket consumer, mounted ABOVE `<Routes>` (P1-1).
 *
 * It used to sit inside `AuthorizationPage`, which `getRoute` instantiates per route — so a
 * navigation that changed the route's `layout` component (`Layout` → `AcademyLayout` /
 * `MinimalLayout`) tore the provider down with the old subtree. The socket itself survived (the
 * manager is a singleton and deliberately does not disconnect on unmount), but the provider's
 * React state did not: `lastMessage`, the dedup map and the processed-message set all reset, so
 * frames published across that window reached nobody. Ask Refabric was the visible casualty — the
 * BE streamed a complete answer into a consumer that no longer existed — but nothing about the
 * failure was chatbot-specific: every WS feature lost frames across such a navigation.
 *
 * Above `<Routes>` and below `<BrowserRouter>` is the whole fix: it outlives every route change,
 * and children can still use router hooks. `SocketListener` deliberately stays inside
 * `AuthorizationPage` — it calls `useNavigate` and must not run on public routes.
 */
const AppSocketProvider = ({ children }: { children: React.ReactNode }) => {
  // `localStorage` is not reactive, and this component no longer gets a free re-render per
  // navigation the way the per-route mount did. Re-read the token whenever auth state moves, so a
  // fresh login connects without a reload. Logout is a full `window.location` load, so it needs
  // nothing here.
  const isAuthorized = useSelector((state: RootState) => state.common.isAuthorized);
  const isCheckingToken = useSelector((state: RootState) => state.common.isCheckingToken);
  const token = React.useMemo(() => getAuthTokenFromLocalStorage(), [isAuthorized, isCheckingToken]);

  return (
    <SocketProvider token={token} secret={process.env.REACT_APP_FRONT_HMAC_KEY as string} frontendKey={process.env.REACT_APP_SOCKET_KEY as string}>
      {children}
    </SocketProvider>
  );
};

/**
 * The real route table: every `ROUTES` entry through `getRoute` (guards, layout, Suspense) plus the
 * legacy redirects, `/docs/*` and the NotFound catch-all. Exported only so
 * `test-utils/renderAppRoutes` can mount the SAME table inside a MemoryRouter — a test that rebuilt
 * it from `ROUTES` would miss every redirect declared below.
 */
export const AppRoutes = () => {
  usePageTracking();

  return (
    <Routes>
      {ROUTES.map((route) => getRoute(route))}

      <Route path="/design" element={<Navigate to="/canvas-projects" replace />} />
      {/* Retired: the tabbed /brand-dnas library. Its three sections now live on their own
          canonical surfaces, so the page had nothing left to be — Style is the Moodboards "My"
          tab, Fabric and Model are standalone asset pages (both still rendered by BrandDnasPage,
          which is why that component stays). `replace` on every hop: these are dead URLs, and a
          pushed entry would send Back straight into the redirect again.
          The fabric/model pair already redirected; style/shared/root join them here. */}
      <Route path={PATHS.BRAND_DNAS} element={<Navigate to={PATHS.MOODBOARDS_MY} replace />} />
      <Route path="/brand-dnas/style" element={<Navigate to={PATHS.MOODBOARDS_MY} replace />} />
      <Route path="/brand-dnas/shared" element={<Navigate to={PATHS.MOODBOARDS_SHARED} replace />} />
      <Route path="/brand-dnas/fabric" element={<Navigate to={PATHS.FABRIC_TRAININGS} replace />} />
      <Route path="/brand-dnas/model" element={<Navigate to={PATHS.PHOTOSHOOT_ASSETS_MODELS} replace />} />
      {/* Anything else under the retired prefix — an unknown tab, a stale deep link — lands on the
          Style surface rather than the 404 page. Last, so the specific hops above win. */}
      <Route path="/brand-dnas/*" element={<Navigate to={PATHS.MOODBOARDS_MY} replace />} />
      {/* Retired: the /new-training landing hub is outside the real training funnel (users start
          training from the library → the specific trainer). Redirect any stale link to the Style
          library. The /new-training/style|fabric|model trainer routes are more specific and still
          match. */}
      <Route path="/new-training" element={<Navigate to={PATHS.MOODBOARDS_MY} replace />} />
      {/* Retired: Resize Images was removed from Bulk Process (REF-5918); Artistic Sketch is the only
          editor left. Redirect any stale /bulk-process/resize link to the Bulk Process landing. */}
      <Route path="/bulk-process/resize" element={<Navigate to="/bulk-process" replace />} />
      {/* Renamed: /trends → /moodboards. Kept so existing deep links keep working. */}
      {/* Every "your moodboard is ready" email ever sent points at `/reports/<id>` — a path this app
          has never registered, so those links have always landed on NotFound (the catch-all at the
          bottom of this file). The backend now sends the board's own URL, but the mails already in
          people's inboxes cannot be rewritten, so the dead path is redirected to the board. REF-6484. */}
      <Route path="/reports/:id" element={<LegacyMoodboardReportRedirect />} />
      {/* The Trends pages' old addresses (until 2026-10-24): their filters (the query) and path params carried over. */}
      <Route path="/trend-history-v2" element={<MovedRedirect to={PATHS.TREND_HISTORY_V2} />} />
      <Route path="/trend-history-v2/:season/:gender" element={<MovedRedirect to={PATHS.TREND_HISTORY_V2_SEASON} />} />
      <Route path="/trend-history-v2/:season/:gender/:city" element={<MovedRedirect to={PATHS.TREND_HISTORY_V2_CITY} />} />
      <Route path="/discover/history" element={<MovedRedirect to={PATHS.TRENDS_HISTORY} />} />
      <Route path="/discover/social-media" element={<MovedRedirect to={PATHS.DISCOVER_SOCIAL} />} />
      {/* Retail Brands' page is retired (2026-10-28): Retail Trends reads the same catalogue, so its addresses land there. */}
      <Route path="/discover/retail-brands" element={<MovedRedirect to={PATHS.RETAIL_TRENDS} />} />
      <Route path={PATHS.DISCOVER_RETAIL_BRANDS} element={<MovedRedirect to={PATHS.RETAIL_TRENDS} />} />
      <Route path="/trends" element={<Navigate to="/moodboards" replace />} />
      <Route path="/trends/:id" element={<Navigate to="/moodboards" replace />} />
      {/* Retired: the legacy tabbed /assets page (sidebar: All / Favorites / Products / Design Assets /
          Created + Integrations) was removed in favor of the clean left-menu routes. Redirect stale
          bookmarks/deep links to their nearest clean equivalent. */}
      <Route path="/assets" element={<Navigate to="/design-assets/fabric" replace />} />
      <Route path="/assets/design-assets" element={<Navigate to="/design-assets/fabric" replace />} />
      <Route path="/assets/design-assets/:type" element={<Navigate to="/design-assets/fabric" replace />} />
      <Route path="/assets/products" element={<Navigate to="/photoshoot-assets/products" replace />} />
      <Route path="/assets/created" element={<Navigate to="/history" replace />} />
      {/* Retired: /design-history (REF-6284). The page was reachable only by typing the URL — no menu,
          no redirect, no programmatic nav linked it — and /history is its live, fuller replacement.
          Internal navigation was gone; direct-URL and bookmark traffic was never measured, which is
          exactly why these are redirects and not deletions. `replace` on every hop, like the other
          retired prefixes above, so Back doesn't bounce straight into the redirect again.
          The wildcard is last so the two specific tab hops win. */}
      <Route path="/design-history" element={<Navigate to={PATHS.HISTORY} replace />} />
      <Route path="/design-history/styles" element={<Navigate to={PATHS.HISTORY} replace />} />
      <Route path="/design-history/patterns" element={<Navigate to={PATHS.HISTORY} replace />} />
      <Route path="/design-history/*" element={<Navigate to={PATHS.HISTORY} replace />} />
      {/* Retired: the standalone Try-On / Mannequin / Ghost combined pages folded into Photoshoot Studio
          (REF-5326). Redirect any stale deep link to the Studio projects list, where history now lives. */}
      <Route path="/photoshoots/tryon/:designId?" element={<Navigate to="/photoshoots/studio" replace />} />
      <Route path="/photoshoots/mannequin/:designId?" element={<Navigate to="/photoshoots/studio" replace />} />
      <Route path="/photoshoots/ghost/:designId?" element={<Navigate to="/photoshoots/studio" replace />} />
      <Route
        path="/photoshoot/:id?"
        element={
          <React.Suspense fallback={null}>
            <PhotoshootRedirect />
          </React.Suspense>
        }
      />

      {/* Developer docs — public (no login), every path under /docs is one generated page. Uses the
          app's theme: RouteThemeWrapper paints it, EmbeddedDocs hands it to the docs to read and set. */}
      <Route
        path="/docs/*"
        element={
          <ErrorBoundary>
            <RouteThemeWrapper>
              <React.Suspense fallback={null}>
                <EmbeddedDocs />
              </React.Suspense>
            </RouteThemeWrapper>
          </ErrorBoundary>
        }
      />

      <Route
        path="*"
        element={
          <ErrorBoundary>
            <React.Suspense fallback={null}>
              <NotFound />
            </React.Suspense>
          </ErrorBoundary>
        }
      />
    </Routes>
  );
};

export default Router;
